import { _isNotEmpty } from '@/littledash';
import type { DataTableColumn, DataTableColumnType, DataTableType } from '../DataTable.model';
import { isFillableColumn } from '../DataTable.util';
import { type Nullable } from '@/model/Common.model';

export const Flag = {
  none: 0,
  display_move: 1 << 0,
  display_edit: 1 << 1,
  display_fill: 1 << 2,
  display_lock: 1 << 3,
  display_delete: 1 << 4,
  move_active: 1 << 5,
  edit_active: 1 << 6,
  fill_active: 1 << 7,
  lock_active: 1 << 8,
  delete_active: 1 << 9,
  locked: 1 << 10,
} as const;
export type Flag = (typeof Flag)[keyof typeof Flag];

/**
 * Checks if all specified flags are set in the given options.
 *
 * @param options - The current flag value to check against.
 * @param flags - One or more flags to verify.
 * @returns `true` if all provided flags are present in options, otherwise `false`.
 */
export const hasFlag = (options: Flag, ...flags: Array<Flag>): boolean => {
  const combined = flags.reduce((acc, flag) => acc | flag, 0);
  return (options & combined) === combined;
};

/**
 * Calculates all menu flags (display and enabled) for a column menu.
 *
 * @param column - The column definition, or undefined.
 * @param tableType - The type of the table, or 'unknown'.
 * @param tableUnit - The unit of the table, or null.
 * @param rowCount - The number of rows in the table.
 * @returns A bitmask containing both display and enabled flags for the column menu.
 */
export const calculateMenuFlags = (
  column: DataTableColumn | undefined,
  tableType: DataTableType | 'unknown',
  tableUnit: Nullable<string>,
  rowCount: number
): Flag => {
  let flags = Flag.none;

  const isTimeTableWithUnit = tableType === 'time' && _isNotEmpty(tableUnit);
  const isLocked = column?.locked ?? false;

  const isEditableType = (type: Nullable<DataTableColumnType>): boolean => {
    switch (type) {
      case 'text':
      case 'number':
      case 'timestamp':
      case 'formula':
      case 'timestampBaseline':
      case 'timestampBaselineRelative':
      case 'sampleDoseTime':
        return true;
      default:
        return false;
    }
  };

  if (isLocked) {
    flags |= Flag.locked;
  }

  flags |= Flag.display_move;
  if (!isLocked && !isTimeTableWithUnit) {
    flags |= Flag.move_active;
  }

  if (isEditableType(column?.type) && !isTimeTableWithUnit) {
    flags |= Flag.display_edit;
  }
  if (!isLocked) {
    flags |= Flag.edit_active;
  }

  if (isFillableColumn(column)) {
    flags |= Flag.display_fill;
  }
  if (!isLocked && rowCount > 0) {
    flags |= Flag.fill_active;
  }

  const isReadOnlyAndUnlocked = (column?.read_only ?? false) && !isLocked;
  if (!isReadOnlyAndUnlocked) {
    flags |= Flag.display_lock;
  }
  flags |= Flag.lock_active;

  flags |= Flag.display_delete;
  if (!isLocked) {
    flags |= Flag.delete_active;
  }

  return flags;
};
